import { useEffect, useRef, useState } from 'react';
import { useAuth } from '@/features/auth/AuthProvider';
import { changeEmployeePassword, updateEmployeeProfile } from '@/features/auth/api';
import { deleteHoliday, fetchHolidays, saveHoliday, deleteProject, fetchProjects, saveProject } from '@/features/settings/api';
import { toPreviewUrl } from '@/lib/fileLinks';
import './settings.css';

function getInitials(name) {
  if (!name) return 'U';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function normalizeRole(role) {
  return String(role || '').trim().toLowerCase().replace(/[_-]+/g, ' ').replace(/\s+/g, ' ');
}

function hasRole(role, roles) {
  const normalized = normalizeRole(role);
  const compact = normalized.replace(/\s+/g, '');
  return roles.some((allowed) => {
    const allowedNormalized = normalizeRole(allowed);
    return allowedNormalized === normalized || allowedNormalized.replace(/\s+/g, '') === compact;
  });
}

function BriefcaseIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="7" width="20" height="14" rx="2" ry="2" />
      <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
    </svg>
  );
}

function UploadIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
      <polyline points="17 8 12 3 7 8"/>
      <line x1="12" y1="3" x2="12" y2="15"/>
    </svg>
  );
}

function LogoutIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
      <polyline points="16 17 21 12 16 7"/>
      <line x1="21" y1="12" x2="9" y2="12"/>
    </svg>
  );
}

function EyeIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
      <circle cx="12" cy="12" r="3"></circle>
    </svg>
  );
}

function EyeOffIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
      <line x1="1" y1="1" x2="23" y2="23"></line>
    </svg>
  );
}

function UserIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  );
}

function ShieldIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
  );
}

function CalendarIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
      <line x1="16" y1="2" x2="16" y2="6" />
      <line x1="8" y1="2" x2="8" y2="6" />
      <line x1="3" y1="10" x2="21" y2="10" />
    </svg>
  );
}

function AllIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="7" height="7" />
      <rect x="14" y="3" width="7" height="7" />
      <rect x="14" y="14" width="7" height="7" />
      <rect x="3" y="14" width="7" height="7" />
    </svg>
  );
}

function ChevronRightIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="9 18 15 12 9 6" />
    </svg>
  );
}

export function SettingsPage() {
  const { user, signOut, refreshSession } = useAuth();
  const fileInputRef = useRef(null);
  
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [uploadSuccess, setUploadSuccess] = useState('');

  const [pwForm, setPwForm] = useState({ current: '', next: '', confirm: '' });
  const [pwError, setPwError] = useState('');
  const [pwSuccess, setPwSuccess] = useState('');
  const [isChangingPw, setIsChangingPw] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const name = user?.['Employee Name'] || user?.Name || 'Employee';
  const avatarUrl = user?.Avatar || user?.Photo || null;
  const employeeId = user?.['Employee ID'] || user?.employeeId;
  const role = user?.Role || 'User';
  const canManageHolidays = hasRole(role, ['Admin', 'Company Admin', 'Super Admin']);
  const canManageProjects = hasRole(role, ['Admin', 'Company Admin', 'Super Admin', 'Manager', 'PC']);
  const department = user?.Department || 'N/A';

  const initialEmail = user?.Email || user?.email || '';
  const initialPhone = user?.['Mobile Number'] || user?.['Phone No'] || '';

  const [email, setEmail] = useState(initialEmail);
  const [phone, setPhone] = useState(initialPhone);
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [isUpdatingProfile, setIsUpdatingProfile] = useState(false);
  const [profileMessage, setProfileMessage] = useState({ type: '', text: '' });
  const [avatarVersion, setAvatarVersion] = useState(0);
  const [holidayState, setHolidayState] = useState({ loading: false, error: '', success: '', rows: [] });
  const [holidayForm, setHolidayForm] = useState({ HolidayID: '', Date: '', Name: '', Description: '' });
  const [savingHoliday, setSavingHoliday] = useState(false);

  const [projectState, setProjectState] = useState({ loading: false, error: '', success: '', rows: [] });
  const [projectForm, setProjectForm] = useState({ ProjectID: '', Name: '', Code: '', Description: '', Status: 'Active' });
  const [savingProject, setSavingProject] = useState(false);
  const [showProjectForm, setShowProjectForm] = useState(false);

  const avatarSrc = avatarUrl
    ? `${toPreviewUrl(avatarUrl)}${toPreviewUrl(avatarUrl).includes('?') ? '&' : '?'}v=${avatarVersion}`
    : null;

  useEffect(() => {
    setEmail(initialEmail);
    setPhone(initialPhone);
    setProfileMessage({ type: '', text: '' });
    setUploadError('');
    setUploadSuccess('');
    setIsEditingProfile(false);
  }, [employeeId, initialEmail, initialPhone]);

  const loadHolidays = async () => {
    if (!canManageHolidays) return;
    setHolidayState((current) => ({ ...current, loading: true, error: '' }));
    try {
      const payload = await fetchHolidays();
      setHolidayState({ loading: false, error: '', success: '', rows: payload.data || [] });
    } catch (error) {
      setHolidayState((current) => ({ ...current, loading: false, error: error.message || 'Failed to load holidays.' }));
    }
  };

  useEffect(() => {
    loadHolidays();
  }, [canManageHolidays]);

  const loadProjects = async () => {
    if (!canManageProjects) return;
    setProjectState((current) => ({ ...current, loading: true, error: '' }));
    try {
      const payload = await fetchProjects();
      setProjectState({ loading: false, error: '', success: '', rows: payload.data || [] });
    } catch (error) {
      setProjectState((current) => ({ ...current, loading: false, error: error.message || 'Failed to load projects.' }));
    }
  };

  useEffect(() => {
    loadProjects();
  }, [canManageProjects]);

  const handleHolidaySubmit = async (event) => {
    event.preventDefault();
    if (!holidayForm.Date || !holidayForm.Name.trim()) {
      setHolidayState((current) => ({ ...current, error: 'Holiday date and name are required.', success: '' }));
      return;
    }
    setSavingHoliday(true);
    setHolidayState((current) => ({ ...current, error: '', success: '' }));
    try {
      await saveHoliday(holidayForm);
      setHolidayForm({ HolidayID: '', Date: '', Name: '', Description: '' });
      const payload = await fetchHolidays();
      setHolidayState({ loading: false, error: '', success: 'Holiday saved successfully.', rows: payload.data || [] });
    } catch (error) {
      setHolidayState((current) => ({ ...current, error: error.message || 'Failed to save holiday.', success: '' }));
    } finally {
      setSavingHoliday(false);
    }
  };

  const handleHolidayEdit = (holiday) => {
    setHolidayForm({
      HolidayID: holiday.HolidayID || holiday['Holiday ID'] || holiday.ID || '',
      Date: holiday.Date || holiday['Holiday Date'] || '',
      Name: holiday.Name || holiday['Holiday Name'] || '',
      Description: holiday.Description || ''
    });
    setHolidayState((current) => ({ ...current, error: '', success: '' }));
  };

  const handleHolidayDelete = async (holiday) => {
    const id = holiday.HolidayID || holiday['Holiday ID'] || holiday.ID;
    if (!id) return;
    setHolidayState((current) => ({ ...current, error: '', success: '' }));
    try {
      await deleteHoliday(id);
      const payload = await fetchHolidays();
      setHolidayState({ loading: false, error: '', success: 'Holiday deleted successfully.', rows: payload.data || [] });
      if (holidayForm.HolidayID === id) setHolidayForm({ HolidayID: '', Date: '', Name: '', Description: '' });
    } catch (error) {
      setHolidayState((current) => ({ ...current, error: error.message || 'Failed to delete holiday.', success: '' }));
    }
  };

  const handleProjectSubmit = async (event) => {
    event.preventDefault();
    if (!projectForm.Name.trim()) {
      setProjectState((current) => ({ ...current, error: 'Project name is required.', success: '' }));
      return;
    }
    setSavingProject(true);
    setProjectState((current) => ({ ...current, error: '', success: '' }));
    try {
      const res = await saveProject(projectForm);
      if (!res.success) {
        setProjectState((current) => ({ ...current, error: res.message || 'Failed to save project.', success: '' }));
        return;
      }
      setProjectForm({ ProjectID: '', Name: '', Code: '', Description: '', Status: 'Active' });
      setShowProjectForm(false);
      const payload = await fetchProjects();
      setProjectState({ loading: false, error: '', success: res.message || 'Project saved successfully.', rows: payload.data || [] });
    } catch (error) {
      setProjectState((current) => ({ ...current, error: error.message || 'Failed to save project.', success: '' }));
    } finally {
      setSavingProject(false);
    }
  };

  const handleProjectEdit = (proj) => {
    setProjectForm({
      ProjectID: proj.ProjectID || proj['Project ID'] || proj.ID || '',
      Name: proj.Name || proj['Project Name'] || '',
      Code: proj.Code || proj['Project Code'] || '',
      Description: proj.Description || '',
      Status: proj.Status || 'Active'
    });
    setProjectState((current) => ({ ...current, error: '', success: '' }));
    setShowProjectForm(true);
  };

  const handleProjectDelete = async (proj) => {
    const id = proj.ProjectID || proj['Project ID'] || proj.ID;
    if (!id) return;
    if (!window.confirm(`Are you sure you want to delete project "${proj.Name || proj['Project Name']}"?`)) return;
    setProjectState((current) => ({ ...current, error: '', success: '' }));
    try {
      const res = await deleteProject(id);
      if (!res.success) {
        setProjectState((current) => ({ ...current, error: res.message || 'Failed to delete project.', success: '' }));
        return;
      }
      const payload = await fetchProjects();
      setProjectState({ loading: false, error: '', success: 'Project deleted successfully.', rows: payload.data || [] });
      if (projectForm.ProjectID === id) {
        setProjectForm({ ProjectID: '', Name: '', Code: '', Description: '', Status: 'Active' });
        setShowProjectForm(false);
      }
    } catch (error) {
      setProjectState((current) => ({ ...current, error: error.message || 'Failed to delete project.', success: '' }));
    }
  };

  // Avatar Upload Logic
  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setUploadError('Please select a valid image file.');
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      setUploadError('Image size should be less than 2MB.');
      return;
    }

    setIsUploading(true);
    setUploadError('');
    setUploadSuccess('');

    try {
      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });

      await updateEmployeeProfile(employeeId, { avatarBase64: base64 });
      setUploadSuccess('Profile picture updated successfully!');
      
      // Refresh user session to get the new avatar
      await refreshSession?.(employeeId);
      setAvatarVersion(version => version + 1);
      
    } catch (err) {
      setUploadError(err.message || 'Failed to upload profile picture.');
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setIsUpdatingProfile(true);
    setProfileMessage({ type: '', text: '' });

    try {
      await updateEmployeeProfile(employeeId, { email, phone });
      setProfileMessage({ type: 'success', text: 'Contact details updated successfully!' });
      setIsEditingProfile(false);
      await refreshSession?.();
    } catch (error) {
      setProfileMessage({ type: 'error', text: error.message || 'Failed to update contact details.' });
    } finally {
      setIsUpdatingProfile(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setPwError('');
    setPwSuccess('');

    if (!pwForm.current) return setPwError('Enter your current password.');
    if (pwForm.next.length < 6) return setPwError('New password must be at least 6 characters.');
    if (pwForm.next !== pwForm.confirm) return setPwError('Passwords do not match.');

    setIsChangingPw(true);
    try {
      await changeEmployeePassword(employeeId, pwForm.current, pwForm.next);
      setPwSuccess('Password changed successfully.');
      setPwForm({ current: '', next: '', confirm: '' });
    } catch (error) {
      setPwError(error.message || 'Unable to change password.');
    } finally {
      setIsChangingPw(false);
    }
  };

  const [activeSection, setActiveSection] = useState(() => {
    if (typeof window !== 'undefined' && window.location.hash) {
      const hash = window.location.hash.replace('#settings-', '').replace('#', '').toLowerCase();
      if (['profile', 'security', 'projects', 'holidays', 'all'].includes(hash)) {
        return hash;
      }
    }
    return 'profile';
  });

  const menuSections = [
    {
      id: 'profile',
      label: 'Profile Settings',
      description: 'Personal info, photo & contact',
      icon: UserIcon,
      tone: 'indigo'
    },
    {
      id: 'security',
      label: 'Security',
      description: 'Password & account protection',
      icon: ShieldIcon,
      tone: 'violet'
    },
    ...(canManageProjects
      ? [
          {
            id: 'projects',
            label: 'Project Settings',
            description: 'Master projects & KRA template mapping',
            icon: BriefcaseIcon,
            badge: 'Admin',
            tone: 'blue'
          }
        ]
      : []),
    ...(canManageHolidays
      ? [
          {
            id: 'holidays',
            label: 'Holiday Settings',
            description: 'Company holidays & alerts schedule',
            icon: CalendarIcon,
            badge: 'Admin',
            tone: 'emerald'
          }
        ]
      : [])
  ];

  const handleSectionClick = (sectionId) => {
    setActiveSection(sectionId);
    try {
      window.history.replaceState(null, '', `#settings-${sectionId}`);
      if (sectionId === 'all') {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else if (window.innerWidth < 1024) {
        const mainEl = document.querySelector('.settings-main-content');
        if (mainEl) {
          mainEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }
    } catch (_) {}
  };

  return (
    <div className="settings-page">
      <div className="settings-layout">
        {/* Left Side Section Menu */}
        <aside className="settings-sidebar">
          <div className="settings-sidebar-user">
            <div className="settings-sidebar-avatar">
              {avatarSrc ? (
                <img src={avatarSrc} alt={name} />
              ) : (
                <span>{getInitials(name)}</span>
              )}
            </div>
            <div className="settings-sidebar-user-meta">
              <strong className="settings-sidebar-user-name" title={name}>{name}</strong>
              <div className="settings-sidebar-badge-row">
                <span className="settings-sidebar-role-pill">{role}</span>
                {employeeId && <span className="settings-sidebar-empid">{employeeId}</span>}
              </div>
            </div>
          </div>

          <div className="settings-sidebar-header">
            <span className="settings-sidebar-title">Settings Menu</span>
            <button
              type="button"
              className={`settings-viewall-pill ${activeSection === 'all' ? 'is-active' : ''}`}
              onClick={() => handleSectionClick(activeSection === 'all' ? 'profile' : 'all')}
              title="Toggle between single section and all sections"
            >
              <AllIcon />
              <span>{activeSection === 'all' ? 'Focus View' : 'View All'}</span>
            </button>
          </div>

          <nav className="settings-sidebar-nav" aria-label="Settings Navigation">
            {menuSections.map((sec) => {
              const isActive = activeSection === sec.id;
              const IconComp = sec.icon;
              return (
                <button
                  key={sec.id}
                  type="button"
                  className={`settings-nav-item settings-nav-item--${sec.tone} ${isActive ? 'is-active' : ''}`}
                  onClick={() => handleSectionClick(sec.id)}
                >
                  <span className="settings-nav-icon-box">
                    <IconComp />
                  </span>
                  <div className="settings-nav-text-col">
                    <span className="settings-nav-label">{sec.label}</span>
                    <span className="settings-nav-desc">{sec.description}</span>
                  </div>
                  {sec.badge && <span className="settings-nav-badge">{sec.badge}</span>}
                  <span className="settings-nav-arrow">
                    <ChevronRightIcon />
                  </span>
                </button>
              );
            })}
          </nav>

          <div className="settings-sidebar-footer">
            <button
              type="button"
              className="settings-sidebar-signout-btn"
              onClick={signOut}
            >
              <LogoutIcon />
              <span>Sign Out</span>
            </button>
          </div>
        </aside>

        {/* Right Side Section Content */}
        <main className={`settings-main-content ${activeSection === 'all' ? 'is-view-all' : ''}`}>
          {(activeSection === 'profile' || activeSection === 'all') && (
            <div className="settings-card settings-card--animated" id="settings-profile">
              <div className="settings-header">
                <h2>Profile Settings</h2>
              </div>

              <div className="settings-avatar-section">
                <div className="settings-avatar-preview">
                  {avatarSrc ? (
                    <img src={avatarSrc} alt={name} />
                  ) : (
                    <span className="settings-avatar-initials">{getInitials(name)}</span>
                  )}
                </div>
                <div className="settings-avatar-actions">
                  <input 
                    type="file" 
                    ref={fileInputRef} 
                    className="settings-file-input" 
                    accept="image/*"
                    onChange={handleFileChange}
                  />
                  <button 
                    className="settings-upload-btn"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={isUploading}
                    type="button"
                  >
                    <UploadIcon />
                    {isUploading ? 'Uploading...' : 'Change Picture'}
                  </button>
                  {uploadError && <div className="settings-message settings-message--error">{uploadError}</div>}
                  {uploadSuccess && <div className="settings-message settings-message--success">{uploadSuccess}</div>}
                </div>
              </div>

              <form onSubmit={handleProfileUpdate} style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                <div className="settings-info-grid">
                  <div className="settings-info-item">
                    <span className="settings-info-label">Full Name</span>
                    <span className="settings-info-value">{name}</span>
                  </div>
                  <div className="settings-info-item">
                    <span className="settings-info-label">Employee ID</span>
                    <span className="settings-info-value">{employeeId}</span>
                  </div>
                  <div className="settings-info-item">
                    <span className="settings-info-label">Role</span>
                    <span className="settings-info-value">{role}</span>
                  </div>
                  <div className="settings-info-item">
                    <span className="settings-info-label">Department</span>
                    <span className="settings-info-value">{department}</span>
                  </div>
                  
                  <div className="settings-form-group settings-info-item">
                    <label className="settings-info-label" style={{ marginBottom: 4 }}>Email</label>
                    {isEditingProfile ? (
                      <input
                        type="email"
                        className="settings-input"
                        value={email}
                        onChange={e => setEmail(e.target.value)}
                        placeholder="Enter email address"
                        autoFocus
                      />
                    ) : (
                      <span className="settings-info-value">{email || 'N/A'}</span>
                    )}
                  </div>
                  <div className="settings-form-group settings-info-item">
                    <label className="settings-info-label" style={{ marginBottom: 4 }}>Phone No</label>
                    {isEditingProfile ? (
                      <input
                        type="text"
                        className="settings-input"
                        value={phone}
                        onChange={e => setPhone(e.target.value)}
                        placeholder="Enter phone number"
                      />
                    ) : (
                      <span className="settings-info-value">{phone || 'N/A'}</span>
                    )}
                  </div>
                </div>
                
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginTop: '16px' }}>
                  {isEditingProfile ? (
                    <>
                      <button 
                        type="submit" 
                        className="settings-submit" 
                        disabled={isUpdatingProfile || (email === initialEmail && phone === initialPhone)}
                        style={{ margin: 0 }}
                      >
                        {isUpdatingProfile ? 'Saving...' : 'Save Changes'}
                      </button>
                      <button 
                        type="button" 
                        className="settings-logout-btn" 
                        style={{ margin: 0, padding: '14px 28px', color: 'var(--wt-text-muted)', border: '1px solid var(--wt-border)', background: 'var(--wt-surface)', boxShadow: 'none' }}
                        onClick={() => {
                          setIsEditingProfile(false);
                          setEmail(initialEmail);
                          setPhone(initialPhone);
                          setProfileMessage({ type: '', text: '' });
                        }}
                      >
                        Cancel
                      </button>
                    </>
                  ) : (
                    <button 
                      type="button" 
                      className="settings-submit" 
                      style={{ margin: 0, background: 'rgba(255, 255, 255, 0.8)', color: '#4f46e5', border: '1px solid rgba(99, 102, 241, 0.4)', boxShadow: '0 4px 12px rgba(99, 102, 241, 0.1)' }}
                      onClick={(e) => { e.preventDefault(); setIsEditingProfile(true); }}
                    >
                      Edit Contact Details
                    </button>
                  )}
                  
                  {profileMessage.text && (
                    <div className={`settings-message settings-message--${profileMessage.type}`} style={{ margin: 0, padding: '8px 12px' }}>
                      {profileMessage.text}
                    </div>
                  )}
                </div>
              </form>
            </div>
          )}

          {(activeSection === 'security' || activeSection === 'all') && (
            <div className="settings-card settings-card--animated" id="settings-security">
              <div className="settings-header">
                <h2>Security</h2>
                <p>Update your password</p>
              </div>

              <form className="settings-form" onSubmit={handlePasswordChange}>
                <div className="settings-form-group">
                  <label>Current Password</label>
                  <div style={{ position: 'relative' }}>
                    <input
                      type={showPassword ? "text" : "password"}
                      className="settings-input"
                      value={pwForm.current}
                      onChange={e => setPwForm(f => ({ ...f, current: e.target.value }))}
                      style={{ width: '100%', paddingRight: '40px' }}
                    />
                    <button 
                      type="button" 
                      onClick={() => setShowPassword(!showPassword)}
                      style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--wt-text-muted)', display: 'flex', alignItems: 'center', padding: 0 }}
                      aria-label={showPassword ? "Hide password" : "Show password"}
                    >
                      {showPassword ? <EyeOffIcon /> : <EyeIcon />}
                    </button>
                  </div>
                </div>
                <div className="settings-form-group">
                  <label>New Password</label>
                  <div style={{ position: 'relative' }}>
                    <input
                      type={showPassword ? "text" : "password"}
                      className="settings-input"
                      value={pwForm.next}
                      onChange={e => setPwForm(f => ({ ...f, next: e.target.value }))}
                      style={{ width: '100%', paddingRight: '40px' }}
                    />
                    <button 
                      type="button" 
                      onClick={() => setShowPassword(!showPassword)}
                      style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--wt-text-muted)', display: 'flex', alignItems: 'center', padding: 0 }}
                      aria-label={showPassword ? "Hide password" : "Show password"}
                    >
                      {showPassword ? <EyeOffIcon /> : <EyeIcon />}
                    </button>
                  </div>
                </div>
                <div className="settings-form-group">
                  <label>Confirm New Password</label>
                  <div style={{ position: 'relative' }}>
                    <input
                      type={showPassword ? "text" : "password"}
                      className="settings-input"
                      value={pwForm.confirm}
                      onChange={e => setPwForm(f => ({ ...f, confirm: e.target.value }))}
                      style={{ width: '100%', paddingRight: '40px' }}
                    />
                    <button 
                      type="button" 
                      onClick={() => setShowPassword(!showPassword)}
                      style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--wt-text-muted)', display: 'flex', alignItems: 'center', padding: 0 }}
                      aria-label={showPassword ? "Hide password" : "Show password"}
                    >
                      {showPassword ? <EyeOffIcon /> : <EyeIcon />}
                    </button>
                  </div>
                </div>
                
                <button type="submit" className="settings-submit" disabled={isChangingPw}>
                  {isChangingPw ? 'Updating...' : 'Change Password'}
                </button>
                
                {pwError && <div className="settings-message settings-message--error">{pwError}</div>}
                {pwSuccess && <div className="settings-message settings-message--success">{pwSuccess}</div>}
              </form>
            </div>
          )}

          {canManageProjects && (activeSection === 'projects' || activeSection === 'all') && (
            <div className="settings-card settings-card--animated" id="settings-projects">
              <div className="settings-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
                <div>
                  <h2>Project Settings</h2>
                  <p>Create and manage master projects. Only projects configured here will be available in the KRA Template dropdown.</p>
                </div>
                {!showProjectForm && (
                  <button
                    type="button"
                    className="settings-btn-add-project"
                    onClick={() => {
                      setProjectForm({ ProjectID: '', Name: '', Code: '', Description: '', Status: 'Active' });
                      setProjectState((c) => ({ ...c, error: '', success: '' }));
                      setShowProjectForm(true);
                    }}
                  >
                    <span>+ Add New Project</span>
                  </button>
                )}
              </div>

              {projectState.success && !showProjectForm && (
                <div className="settings-message settings-message--success" style={{ margin: '14px 0 18px 0' }}>
                  {projectState.success}
                </div>
              )}

              {showProjectForm && (
                <div className="settings-project-form-container">
                  <div className="settings-project-form-header">
                    <div className="settings-project-form-title-group">
                      <div className="settings-project-form-icon-badge">
                        {projectForm.ProjectID ? '✏️' : '📁'}
                      </div>
                      <div className="settings-project-form-title-text">
                        <h3>{projectForm.ProjectID ? 'Edit Project' : 'Add New Project'}</h3>
                        <p>{projectForm.ProjectID ? 'Update project configuration and status' : 'Configure a master project for KRA assignment'}</p>
                      </div>
                    </div>
                    <button
                      type="button"
                      className="settings-project-close-btn"
                      onClick={() => {
                        setShowProjectForm(false);
                        setProjectForm({ ProjectID: '', Name: '', Code: '', Description: '', Status: 'Active' });
                      }}
                      title="Close Form"
                    >
                      ✕
                    </button>
                  </div>

                  <form onSubmit={handleProjectSubmit}>
                    <input type="hidden" value={projectForm.ProjectID} readOnly />
                    <div className="settings-project-form-grid">
                      <div className="settings-project-field">
                        <label className="settings-project-field-label">
                          Project Name <span className="required-star">*</span>
                        </label>
                        <input
                          type="text"
                          className="settings-project-input"
                          value={projectForm.Name}
                          onChange={(e) => setProjectForm((f) => ({ ...f, Name: e.target.value }))}
                          placeholder="e.g. Website Redesign, SEO Campaign"
                          required
                          autoFocus
                        />
                        <span className="settings-project-field-help">Required master project name</span>
                      </div>

                      <div className="settings-project-field">
                        <label className="settings-project-field-label">
                          Project Code <span className="field-hint">(Optional)</span>
                        </label>
                        <input
                          type="text"
                          className="settings-project-input"
                          value={projectForm.Code}
                          onChange={(e) => setProjectForm((f) => ({ ...f, Code: e.target.value }))}
                          placeholder="e.g. PRJ-001 or WR"
                        />
                        <span className="settings-project-field-help">Short code or ticket prefix</span>
                      </div>

                      <div className="settings-project-field">
                        <label className="settings-project-field-label">Status</label>
                        <select
                          className="settings-project-select"
                          value={projectForm.Status}
                          onChange={(e) => setProjectForm((f) => ({ ...f, Status: e.target.value }))}
                        >
                          <option value="Active">Active</option>
                          <option value="Inactive">Inactive</option>
                        </select>
                        <span className="settings-project-field-help">Active projects appear in KRA dropdown</span>
                      </div>

                      <div className="settings-project-field settings-project-field--full">
                        <label className="settings-project-field-label">
                          Description / Scope <span className="field-hint">(Optional)</span>
                        </label>
                        <input
                          type="text"
                          className="settings-project-input"
                          value={projectForm.Description}
                          onChange={(e) => setProjectForm((f) => ({ ...f, Description: e.target.value }))}
                          placeholder="Brief notes, client reference, or deliverables"
                        />
                      </div>

                      <div className="settings-project-actions-bar">
                        <button
                          type="button"
                          className="settings-btn-cancel-project"
                          onClick={() => {
                            setShowProjectForm(false);
                            setProjectForm({ ProjectID: '', Name: '', Code: '', Description: '', Status: 'Active' });
                          }}
                        >
                          Cancel
                        </button>
                        <button type="submit" className="settings-btn-save-project" disabled={savingProject}>
                          {savingProject ? 'Saving...' : projectForm.ProjectID ? '✓ Update Project' : '+ Save Project'}
                        </button>
                      </div>

                      {projectState.error && (
                        <div className="settings-message settings-message--error" style={{ gridColumn: '1 / -1' }}>
                          {projectState.error}
                        </div>
                      )}
                      {projectState.success && (
                        <div className="settings-message settings-message--success" style={{ gridColumn: '1 / -1' }}>
                          {projectState.success}
                        </div>
                      )}
                    </div>
                  </form>
                </div>
              )}

              <div className="settings-project-list">
                {projectState.loading ? (
                  <div className="settings-project-empty">Loading projects...</div>
                ) : projectState.rows.length ? (
                  projectState.rows.map((proj) => {
                    const id = proj.ProjectID || proj['Project ID'] || proj.ID;
                    const isActive = proj.Status === 'Active';
                    return (
                      <div className="settings-project-card-item" key={id || proj.Name}>
                        <div className="settings-project-card-left">
                          <div className="settings-project-card-icon">
                            📁
                          </div>
                          <div className="settings-project-card-info">
                            <div className="settings-project-card-title-row">
                              <span className="settings-project-card-name">{proj.Name || proj['Project Name']}</span>
                              {proj.Code && <span className="settings-project-card-code">{proj.Code}</span>}
                              <span className={`settings-project-card-status ${isActive ? 'settings-project-card-status--active' : 'settings-project-card-status--inactive'}`}>
                                {proj.Status || 'Active'}
                              </span>
                            </div>
                            {proj.Description && <span className="settings-project-card-desc">{proj.Description}</span>}
                          </div>
                        </div>
                        <div className="settings-project-card-actions">
                          <button
                            type="button"
                            className="settings-btn-edit-proj"
                            onClick={() => handleProjectEdit(proj)}
                          >
                            ✏️ Edit
                          </button>
                          <button
                            type="button"
                            className="settings-btn-del-proj"
                            onClick={() => handleProjectDelete(proj)}
                          >
                            🗑️ Delete
                          </button>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div className="settings-project-empty">
                    <p style={{ margin: '0 0 12px 0' }}>No projects configured yet.</p>
                    {!showProjectForm && (
                      <button
                        type="button"
                        className="settings-btn-add-project"
                        style={{ padding: '10px 20px', fontSize: '13px' }}
                        onClick={() => {
                          setProjectForm({ ProjectID: '', Name: '', Code: '', Description: '', Status: 'Active' });
                          setShowProjectForm(true);
                        }}
                      >
                        + Add First Project
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {canManageHolidays && (activeSection === 'holidays' || activeSection === 'all') && (
            <div className="settings-card settings-card--animated" id="settings-holidays">
              <div className="settings-header">
                <h2>Holiday Settings</h2>
                <p>Add company holidays. On holiday dates, WhatsApp alerts are skipped and attendance shows Holiday.</p>
              </div>

              <form className="settings-form settings-holiday-form" onSubmit={handleHolidaySubmit}>
                <input type="hidden" value={holidayForm.HolidayID} readOnly />
                <div className="settings-form-group">
                  <label>Holiday Date</label>
                  <input type="date" className="settings-input" value={holidayForm.Date} onChange={(event) => setHolidayForm((current) => ({ ...current, Date: event.target.value }))} />
                </div>
                <div className="settings-form-group">
                  <label>Holiday Name</label>
                  <input type="text" className="settings-input" value={holidayForm.Name} onChange={(event) => setHolidayForm((current) => ({ ...current, Name: event.target.value }))} placeholder="e.g. Diwali, Independence Day" />
                </div>
                <div className="settings-form-group">
                  <label>Description</label>
                  <input type="text" className="settings-input" value={holidayForm.Description} onChange={(event) => setHolidayForm((current) => ({ ...current, Description: event.target.value }))} placeholder="Optional note" />
                </div>
                <div className="settings-holiday-actions">
                  <button type="submit" className="settings-submit" disabled={savingHoliday}>{savingHoliday ? 'Saving...' : holidayForm.HolidayID ? 'Update Holiday' : 'Add Holiday'}</button>
                  {holidayForm.HolidayID && <button type="button" className="settings-logout-btn settings-secondary-btn" onClick={() => setHolidayForm({ HolidayID: '', Date: '', Name: '', Description: '' })}>Cancel Edit</button>}
                </div>
                {holidayState.error && <div className="settings-message settings-message--error">{holidayState.error}</div>}
                {holidayState.success && <div className="settings-message settings-message--success">{holidayState.success}</div>}
              </form>

              <div className="settings-holiday-list">
                {holidayState.loading ? (
                  <div className="settings-holiday-empty">Loading holidays...</div>
                ) : holidayState.rows.length ? (
                  holidayState.rows.map((holiday) => {
                    const id = holiday.HolidayID || holiday['Holiday ID'] || holiday.ID;
                    return (
                      <div className="settings-holiday-row" key={id || holiday.Date}>
                        <div>
                          <strong>{holiday.Name || holiday['Holiday Name'] || 'Holiday'}</strong>
                          <span>{holiday.Date || holiday['Holiday Date']} {holiday.Description ? ' - ' + holiday.Description : ''}</span>
                        </div>
                        <div className="settings-holiday-row-actions">
                          <button type="button" onClick={() => handleHolidayEdit(holiday)}>Edit</button>
                          <button type="button" onClick={() => handleHolidayDelete(holiday)}>Delete</button>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div className="settings-holiday-empty">No holidays added yet.</div>
                )}
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
